import type { CoreFileItem } from "../core";
import { VerityApi } from "../api/fetch";

export const REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const REFERENCE_MATERIAL_ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,application/pdf";

export async function uploadFileToFolder(
	file: File,
	folderKey: string,
	getAPI: VerityApi["getReferenceMaterialUploadUrl"],
): Promise<void> {
	const { uploadUrl } = await getAPI({
		folderKey,
		fileName: file.name,
		mimeType: file.type,
		fileSize: file.size,
	});
	const uploadResponse = await fetch(uploadUrl, {
		method: "PUT",
		headers: { "Content-Type": file.type },
		body: file,
	});
	if (!uploadResponse.ok) {
		throw new Error("File upload failed");
	}
}

export async function uploadFilesToTempFolder(
	files: CoreFileItem[] | undefined,
	getAPI: VerityApi["getReferenceMaterialUploadUrl"],
): Promise<string | undefined> {
	if (!files || files.length === 0) {
		return undefined;
	}

	const filteredFiles = files.filter((file) => file.nativeFile && file.size <= REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES);
	if (filteredFiles.length === 0) {
		return undefined;
	}
	const tempKey = crypto.randomUUID();
	for (const file of filteredFiles) {
		if (!file.nativeFile) continue;
		await uploadFileToFolder(file.nativeFile, tempKey, getAPI);
	}
	return tempKey;
}

export async function saveFilesToFolder(
	folderKey: string,
	files: CoreFileItem[] | undefined,
	getAPI: VerityApi["getReferenceMaterialUploadUrl"],
	moveAPI: VerityApi["moveReferenceMaterialsFromTemp"],
	deleteAPI: VerityApi["deleteAllReferenceMaterials"],
): Promise<void> {
	const allFiles = files ?? [];
	const filesToUpload = allFiles.filter(
		(file) => file.nativeFile && file.size <= REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES,
	);

	// If there are no files to upload, delete existing materials at folderKey (if any) and return early.
	if (filesToUpload.length === 0) {
		await deleteAPI(folderKey);
		return;
	}

	// 1. Upload all new files to a temp folder first. If any upload fails, the
	//    existing materials at folderKey are left completely intact.
	const tempKey = crypto.randomUUID();
	for (const file of filesToUpload) {
		await uploadFileToFolder(file.nativeFile!, tempKey, getAPI);
	}

	// 2. All uploads succeeded — request the server to replace existing reference
	//    materials at folderKey with the temp files (not strictly atomic).
	await moveAPI({ temporaryKey: tempKey, quizId: folderKey });
}

export async function fetchReferenceMaterialsWithNativeFiles(
	folderKey: string,
	getAPI: VerityApi["getReferenceMaterials"],
): Promise<CoreFileItem[]> {
	const files = await getAPI(folderKey);
	const fileList = Array.isArray(files) ? files : [];
	return Promise.all(
		fileList.map(async (file): Promise<CoreFileItem> => {
			if (!file.url) return file;
			try {
				const response = await fetch(file.url);
				if (!response.ok) return file;
				const blob = await response.blob();
				return {
					...file,
					nativeFile: new File([blob], file.name, { type: blob.type }),
				} as CoreFileItem;
			} catch {
				return file;
			}
		}),
	);
}
